import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import buildPATHS from '../../utils/paths';
import styles from './AppErrorPage.module.scss';
import { getTranslation } from '../../utils/functions';
import { useSelector } from 'react-redux';
import { validationResponseSelector } from '../../store/selectors/formConfigSelectors';
import { RootStore } from '../../store/reducers';
import { getFormattedDate, getFormattedTime } from '../../utils/dateUtils';
import { quotaExceededMessageSelector } from '../../store/selectors/globalConfigSelectors';
import {
  appErrorSelector,
  languageSelector,
} from '../../store/selectors/appSelectors';
import { APP_ERRORS } from '../../constants/backendResponse';

enum ERROR_TYPE {
  FORM_NOT_OPENED,
  FORM_EXPIRED,
  QUOTA_EXCEED,
}

const AppErrorPage: React.FC = () => {
  const PATHS = buildPATHS();
  const { validationResponse, quotaExceededMessage, language, appError } =
    useSelector((state: RootStore) => ({
      validationResponse: validationResponseSelector(state),
      quotaExceededMessage: quotaExceededMessageSelector(state),
      language: languageSelector(state),
      appError: appErrorSelector(state),
    }));

  const [showErrorType, setShowErrorType] = useState<ERROR_TYPE>();

  useEffect(() => {
    if (validationResponse?.startTimeValidationFailed) {
      setShowErrorType(ERROR_TYPE.FORM_NOT_OPENED);
    } else if (validationResponse?.endTimeValidationFailed) {
      setShowErrorType(ERROR_TYPE.FORM_EXPIRED);
    }
  }, [validationResponse]);

  const getQuotaExceededMessage = (): string => {
    const backendMessage = quotaExceededMessage?.[language];
    const defaultMessage = getTranslation('QUOTA_EXCEEDED_MESSAGE');

    return backendMessage || defaultMessage;
  };

  const decodeHtml = (html) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = html;
    return textarea.value;
  };

  const renderQuoteExceedMsg = () => {
    const message = getQuotaExceededMessage();
    return (
      <div
        className="hid-spacing__mt-06 m-t-24 hid-flex hid-flex-column hid-flex-ai-center text-wrap-balanced"
        dangerouslySetInnerHTML={{
          __html: decodeHtml(message),
        }}
      ></div>
    );
  };

  const renderFormNotOpenMsg = () => {
    return (
      <>
        <div>{`${getTranslation('FORM_NOT_OPENED_MSG1')} `}</div>
        <div className="m-t-8">
          {`${getTranslation('FORM_NOT_OPENED_DURATION_MSG1')} ${getFormattedTime(validationResponse?.startDate, validationResponse?.startTime)} ${getTranslation('ON')} ${getFormattedDate(validationResponse?.startDate, validationResponse?.startTime)}
           ${getTranslation('AND')} ${getFormattedTime(validationResponse?.endDate, validationResponse?.endTime)}
            ${getTranslation('ON')} 
            ${getFormattedDate(validationResponse?.endDate, validationResponse?.endTime)}.`}
        </div>
        <div className="m-t-8">{getTranslation('FORM_NOT_OPENED_MSG2')}</div>
        <div className="m-t-16">{`${getTranslation('THANK_YOU')}.`}</div>
      </>
    );
  };
  const renderExpiredMsg = () => {
    return (
      <>
        <div className="m-t-8">
          {`${getTranslation('REGISTRATION_LINK_EXPIRED1')} ${getFormattedTime(validationResponse?.endDate, validationResponse?.endTime)} ${getTranslation('ON')} ${getFormattedDate(validationResponse?.endDate, validationResponse?.endTime)}`}
          .
        </div>
        <div className="m-t-8">
          {getTranslation('REGISTRATION_LINK_EXPIRED2')}
        </div>
        <div className="m-t-16">{`${getTranslation('THANK_YOU')}.`}</div>
      </>
    );
  };

  const renderUserAccountNotApprovedMsg = () => {
    return (
      <>
        <div className="hid-spacing__mt-06 m-t-24 hid-flex hid-flex-column hid-flex-ai-center text-wrap-balanced">
          <div> {getTranslation('USER_ACCOUNT_NOT_APPROVED_MSG1')}</div>
          <div>{getTranslation('USER_ACCOUNT_NOT_APPROVED_MSG2')}</div>
        </div>
      </>
    );
  };
  const renderUserAccountDisabilityCategoryMismatchMsg = () => {
    return (
      <>
        <div className="hid-spacing__mt-06 m-t-24 hid-flex hid-flex-column hid-flex-ai-center text-wrap-balanced">
          <div>
            {' '}
            {getTranslation('USER_ACCOUNT_DISABILITY_CATEGORY_MISMATCH_MSG1')}
          </div>
          <div>
            {getTranslation('USER_ACCOUNT_DISABILITY_CATEGORY_MISMATCH_MSG2')}
          </div>
        </div>
      </>
    );
  };
  const renderUserAccountRejectedMsg = () => {
    return (
      <>
        <div className="hid-spacing__mt-06 m-t-24 hid-flex hid-flex-column hid-flex-ai-center text-wrap-balanced">
          <div>{getTranslation('USER_ACCOUNT_REJECTED_MSG1')}</div>
          <div>{getTranslation('USER_ACCOUNT_NOT_APPROVED_MSG2')}</div>
        </div>
      </>
    );
  };

  const getGuestModeNotAllowedMessage = (): React.ReactNode => {
    const message = getTranslation('GUEST_MODE_DISABLED_MSG');
    return (
      <div
        className="hid-spacing__mt-06 m-t-24 hid-flex hid-flex-column hid-flex-ai-center text-wrap-balanced"
        dangerouslySetInnerHTML={{
          __html: decodeHtml(message),
        }}
      ></div>
    );
  };

  const renderComppnent = () => {
    if (appError === APP_ERRORS.QUOTA_EXCEEDED) {
      return renderQuoteExceedMsg();
    }
    if (appError === APP_ERRORS.USER_ACCOUNT_NOT_APPROVED) {
      return renderUserAccountNotApprovedMsg();
    }
    if (appError === APP_ERRORS.USER_ACCOUNT_DISABILITY_CATEGORY_MISMATCH) {
      return renderUserAccountDisabilityCategoryMismatchMsg();
    }
    if (appError === APP_ERRORS.USER_ACCOUNT_REJECTED) {
      return renderUserAccountRejectedMsg();
    }

    if (appError === APP_ERRORS.GUEST_MODE_DISABLED_ERROR) {
      return getGuestModeNotAllowedMessage();
    }

    switch (showErrorType) {
      case ERROR_TYPE.FORM_NOT_OPENED:
        return renderFormNotOpenMsg();

        break;
      case ERROR_TYPE.FORM_EXPIRED:
        return renderExpiredMsg();

        break;

      default:
        break;
    }
  };
  return (
    <div className={styles.wrapper}>
      <div className={styles.imgWrapper}>
        <img
          src="assets/entrance-error.svg"
          alt="Expired"
          className={styles.expiredImg}
        />
      </div>
      <div className="hid-spacing__mt-06 m-t-24 hid-flex hid-flex-column hid-flex-ai-center text-wrap-balanced">
        {renderComppnent()}
      </div>
    </div>
  );
};

export default AppErrorPage;
